import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "./ui/command";

/**
 * SearchableSelect - searchable dropdown (uses shadcn Command + Popover).
 * options: [{value, label}]
 * value: selected value
 * allowCreate: if true, shows "+ Create X" when no match
 */
export const SearchableSelect = ({
    options = [],
    value,
    onValueChange,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyText = "Not found.",
    allowCreate = false,
    createLabel = "Add",
    onCreate,
    disabled = false,
    testId = "searchable-select",
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

    const filtered = useMemo(() => {
        if (!search) return options;
        const s = search.toLowerCase();
        return options.filter((o) => (o.label || "").toLowerCase().includes(s));
    }, [options, search]);

    const showCreate = allowCreate && search && !filtered.some((o) => (o.label || "").toLowerCase() === search.toLowerCase());

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild disabled={disabled}>
                <button
                    type="button"
                    data-testid={testId}
                    disabled={disabled}
                    className="input-modern !flex !items-center !justify-between !text-left disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span className={selected ? "text-foreground" : "text-muted-foreground"}>
                        {selected ? selected.label : placeholder}
                    </span>
                    <ChevronsUpDown className="h-4 w-4 text-muted-foreground opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={search}
                        onValueChange={setSearch}
                        data-testid={`${testId}-search`}
                    />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {filtered.map((o) => (
                                <CommandItem
                                    key={o.value}
                                    value={o.value}
                                    data-testid={`${testId}-option-${o.value}`}
                                    onSelect={() => {
                                        onValueChange(o.value);
                                        setOpen(false);
                                        setSearch("");
                                    }}
                                    className="cursor-pointer"
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${value === o.value ? "opacity-100" : "opacity-0"}`}
                                    />
                                    {o.label}
                                </CommandItem>
                            ))}
                            {showCreate && (
                                <CommandItem
                                    data-testid={`${testId}-create`}
                                    onSelect={() => {
                                        onCreate && onCreate(search);
                                        setSearch("");
                                        setOpen(false);
                                    }}
                                    className="cursor-pointer text-primary font-semibold"
                                >
                                    <Plus className="mr-2 h-4 w-4" />
                                    {createLabel}: "{search}"
                                </CommandItem>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
